import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { forgotPassword, confirmForgotPassword } from "@/lib/auth";
import { passwordSchema } from "@/lib/auth/password-schema";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { Loader2, MoveLeft, Eye, EyeOff } from "lucide-react";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { YesChefLogo } from "@/components/common/YesChefLogo";

const requestSchema = z.object({
  email: z.string().email({ error: () => t`Invalid email address` }),
});

const resetSchema = z
  .object({
    code: z.string().min(1, { error: () => t`Verification code is required` }),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    error: () => t`Passwords do not match`,
    path: ["confirmPassword"],
  });

type RequestForm = z.infer<typeof requestSchema>;
type ResetForm = z.infer<typeof resetSchema>;

export const Route = createFileRoute("/(public)/forgot-password")({
  component: ForgotPasswordComponent,
});

function ForgotPasswordComponent() {
  const navigate = useNavigate();
  const { i18n } = useLingui();

  const [step, setStep] = useState<"request" | "reset">("request");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // `defaultValues` matter now that the fields are driven by `FormField`'s
  // controller: without them the inputs would start uncontrolled and React
  // would warn on the first keystroke.
  const requestForm = useForm<RequestForm>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: "" },
  });

  const resetForm = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
    defaultValues: { code: "", newPassword: "", confirmPassword: "" },
  });

  const requestMutation = useMutation({
    mutationFn: forgotPassword,
    onSuccess: (response) => {
      if (response.success) {
        toast.success(
          t`Verification code sent to ${response.destination || i18n._("your email")}`,
        );
        setStep("reset");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to send reset code`);
    },
  });

  const resetMutation = useMutation({
    mutationFn: confirmForgotPassword,
    onSuccess: () => {
      toast.success(t`Password reset successful`);
      navigate({ to: "/login" });
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Password reset failed`);
    },
  });

  const onRequestSubmit = (data: RequestForm) => {
    setEmail(data.email);
    requestMutation.mutate({ email: data.email });
  };

  const onResetSubmit = (data: ResetForm) => {
    resetMutation.mutate({ email, code: data.code, newPassword: data.newPassword });
  };

  const isPending = requestMutation.isPending || resetMutation.isPending;

  const goToLoginLink = (
    <Link
      to="/login"
      className="flex items-center gap-2 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-opacity hover:opacity-70 text-(--gray-600)"
    >
      <MoveLeft className="h-5 w-5" />
      {i18n._("Go to Login")}
    </Link>
  );

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-between px-8 py-20 bg-(--brand-white)"
    >
      <YesChefLogo />

      <div className="flex w-full max-w-[488px] flex-col gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1
            className="font-semibold leading-tight text-(--brand-black)"
            style={{ fontSize: 48 }}
          >
            {step === "request"
              ? i18n._("Forgot password?")
              : i18n._("Create new password")}
          </h1>
          <p className="text-base text-(--gray-500)">
            {step === "request"
              ? i18n._("Enter your email to receive a reset code.")
              : i18n._("Enter the code sent to your email and choose a new password.")}
          </p>
        </div>

        {step === "request" && (
          <Form {...requestForm}>
            <form
              onSubmit={requestForm.handleSubmit(onRequestSubmit)}
              className="flex flex-col gap-2.5"
              noValidate
            >
              <FormField
                control={requestForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._("Email")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={i18n._("Enter your email address...")}
                        className="figma-input"
                        disabled={isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <button
                type="submit"
                disabled={isPending}
                className="mt-1 flex h-11 w-full items-center justify-center rounded-[8px] text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 bg-(--brand-black)"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {i18n._("Sending...")}
                  </>
                ) : (
                  i18n._("Send reset code")
                )}
              </button>

              <div className="flex justify-center">{goToLoginLink}</div>
            </form>
          </Form>
        )}

        {step === "reset" && (
          <Form {...resetForm}>
            <form
              onSubmit={resetForm.handleSubmit(onResetSubmit)}
              className="flex flex-col gap-2.5"
              noValidate
            >
              <FormField
                control={resetForm.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._("Verification code")}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder={i18n._("Enter the 6-digit code...")}
                        className="figma-input"
                        disabled={isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={resetForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._("Create new password")}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder={i18n._("Create new password...")}
                          className="figma-input pr-10"
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-(--gray-400)"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={resetForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._("Confirm new password")}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={i18n._("Confirm new password...")}
                          className="figma-input pr-10"
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-(--gray-400)"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <button
                type="submit"
                disabled={isPending}
                className="mt-1 flex h-11 w-full items-center justify-center rounded-[8px] text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 bg-(--brand-black)"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {i18n._("Saving...")}
                  </>
                ) : (
                  i18n._("Save changes")
                )}
              </button>

              <div className="flex justify-center">{goToLoginLink}</div>
            </form>
          </Form>
        )}
      </div>

      <div aria-hidden className="h-10" />

    </div>
  );
}
